import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Restaurant } from '../../core/model/restaurant';
import { activeFilterCount } from '../../core/model/search';
import type { SortOption } from '../../core/model/search';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useCriteria } from '../../state/CriteriaContext';
import { UiStates, type UiState } from '../../state/uiState';
import { useAsync, useDebounced } from '../../state/useAsync';
import type { Page } from '../../data/repository/repositories';
import { FiltersSheet } from '../components/FiltersSheet';
import { RestaurantCard } from '../components/RestaurantCard';
import { SearchCriteriaBar } from '../components/SearchCriteriaBar';
import { EmptyState, StateContainer } from '../components/StateContainer';
import { FilterIcon, SearchIcon } from '../components/icons';

const SORT_LABELS: Record<SortOption, string> = {
  RELEVANCE: S.sortRelevance,
  RATING: S.sortRating,
  DISTANCE: S.sortDistance,
  PRICE_LOW: S.sortPriceLow,
  PRICE_HIGH: S.sortPriceHigh,
};

export function SearchScreen() {
  const navigate = useNavigate();
  const { restaurants } = useContainer();
  const { criteria, setQuery, setFilters, clearFilters, setSort } = useCriteria();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Una petición por pausa de escritura, no por tecla.
  const debouncedQuery = useDebounced(criteria.query);
  const searchCriteria = useMemo(() => ({ ...criteria, query: debouncedQuery }), [criteria, debouncedQuery]);

  const { state, reload } = useAsync<Page<Restaurant>, Page<Restaurant>>(
    () => restaurants.search(searchCriteria),
    [
      restaurants,
      debouncedQuery,
      criteria.location,
      criteria.date,
      criteria.time,
      criteria.partySize,
      criteria.filters,
      criteria.sort,
    ],
    (page): UiState<Page<Restaurant>> =>
      page.items.length === 0 ? UiStates.empty() : UiStates.success(page),
  );

  const openRestaurant = useCallback((id: string) => navigate(`/restaurante/${id}`), [navigate]);
  const filterCount = activeFilterCount(criteria.filters);
  const resultCount = state.status === 'SUCCESS' ? state.data.total : null;

  return (
    <div className="stack">
      <div className="row">
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <input
            className="field__control"
            type="search"
            value={criteria.query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={S.homeSearchHint}
            aria-label={S.homeSearchHint}
          />
        </div>
        <button type="button" className="btn btn--outline" onClick={() => setFiltersOpen(true)}>
          <FilterIcon size={18} />
          {S.searchFilters}
          {filterCount > 0 && <span className="filter-badge">{filterCount}</span>}
        </button>
      </div>

      <SearchCriteriaBar />

      <div className="row">
        <label className="field__label" htmlFor="sort">
          {S.searchSort}
        </label>
        <select
          id="sort"
          className="field__control"
          style={{ width: 'auto', minWidth: 200 }}
          value={criteria.sort}
          onChange={(event) => setSort(event.target.value as SortOption)}
        >
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="spacer" />
        {resultCount !== null && <span className="muted">{S.searchResultsCount(resultCount)}</span>}
      </div>

      <StateContainer
        state={state}
        onRetry={reload}
        empty={
          <EmptyState
            icon="🍽"
            title={S.searchEmptyTitle}
            body={S.searchEmptyBody}
            // La salida se ajusta a la causa: si hay filtros, quitarlos; si no, cambiar la hora,
            // que es lo único que puede estar acotando los resultados.
            action={
              filterCount > 0
                ? { label: S.searchClearFilters, onClick: clearFilters }
                : { label: S.searchEmptySuggestionTime, onClick: () => setFiltersOpen(false) }
            }
          />
        }
      >
        {(page) => (
          <div className="restaurant-grid">
            {page.items.map((restaurant) => (
              <RestaurantCard
                key={restaurant.id}
                restaurant={restaurant}
                onOpen={openRestaurant}
                showSlots
                onSelectSlot={(r) => navigate(`/reservar/${r.id}`)}
              />
            ))}
          </div>
        )}
      </StateContainer>

      {filtersOpen && (
        <FiltersSheet
          initial={criteria.filters}
          resultCount={resultCount}
          onApply={setFilters}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      <p className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <SearchIcon size={14} /> {S.demoBanner}
      </p>
    </div>
  );
}
